import { getFileUrl } from "@/api/oss";
import { openImageWindowWithBox } from "@/components/file_process_js/utils";
import { ElMessage } from "element-plus";

/**
 * @class OcrResultProcessor
 * @description
 * Processes complex OCR data which can include both standalone text boxes and tables.
 * It flattens all text elements, sorts them in natural reading order,
 * and provides functionalities to get the full concatenated text, per-page text,
 * and find the coordinates of any substring within the full text.
 */
export class OcrResultProcessor {
    /**
     * @param {Array<Object>} ocrData - The raw OCR data from your API.
     * @param {Object} [options] - Configuration options.
     * @param {number} [options.lineTolerance=10] - The pixel tolerance to consider two boxes to be on the same line.
     */
    constructor(ocrData, { lineTolerance = 10 } = {}) {
        // Ensure rawData is a valid array
        this.rawData = Array.isArray(ocrData) ? ocrData : [];
        this.lineTolerance = lineTolerance;
        this.processedData = []; // This will store the metadata for each text chunk
        this.fullText = "";
        this.pagesText = []; // This will store the concatenated text for each page
        this._process();
    }

    /**
     * @private
     * The core processing method. It iterates through pages, flattens `boxes` and `table.sub_boxes`,
     * sorts them, and builds the final `fullText`, `pagesText`, and `processedData`.
     */
    _process() {
        let currentFullText = "";
        const allProcessedItems = [];

        this.rawData.forEach((pageData, fileIndex) => {
            let content = null
            const { img_key, text_content } = pageData;
            content = text_content ? text_content : pageData['content'];

            if (!content) return;

            // 1. Unify all text elements (from top-level boxes and tables) into a single list
            const elementsOnPage = [];

            // Add top-level boxes
            if (content.boxes && Array.isArray(content.boxes)) {
                content.boxes.forEach(box => {
                    const [coordinates, [text]] = box;
                    if (text && text.trim() !== "") {
                        elementsOnPage.push({
                            text: text,
                            coordinates: coordinates,
                            y: coordinates[0][1], // Top-left Y for sorting
                            x: coordinates[0][0], // Top-left X for sorting
                        });
                    }
                });
            }

            // Add table sub_boxes
            if (content.table && Array.isArray(content.table)) {
                content.table.forEach(table => {
                    if (table.sub_boxes && Array.isArray(table.sub_boxes)) {
                        table.sub_boxes.forEach(subBox => {
                            // The `content` property of a sub_box holds the text
                            const text = subBox.content;
                            // The `box` property holds the coordinates
                            const coordinates = subBox.box;
                            if (text && text.trim() !== "") {
                                elementsOnPage.push({
                                    text: text,
                                    coordinates: coordinates,
                                    y: coordinates[0][1], // Top-left Y
                                    x: coordinates[0][0], // Top-left X
                                });
                            }
                        });
                    }
                });
            }

            // 2. Sort all elements on the page by reading order (top-to-bottom, left-to-right)
            const sortedElements = elementsOnPage.sort((a, b) => {
                if (Math.abs(a.y - b.y) <= this.lineTolerance) {
                    return a.x - b.x; // Same line, sort by X
                } else {
                    return a.y - b.y; // Different lines, sort by Y
                }
            });

            // Add a separator between pages if this is not the first page
            if (fileIndex > 0 && currentFullText.length > 0) {
                currentFullText += "\n\n";
            }

            // 3. Build the fullText string, the per-page text, and the processedData array
            let currentPageText = ""; // Holds the text for the current page
            sortedElements.forEach((element, elementIndex) => {
                // Determine if a space or a newline is needed before this element
                if (elementIndex > 0) {
                    const prevElement = sortedElements[elementIndex - 1];
                    if (Math.abs(prevElement.y - element.y) <= this.lineTolerance) {
                        currentFullText += " "; // Same line
                        currentPageText += " ";
                    } else {
                        currentFullText += "\n"; // New line
                        currentPageText += "\n";
                    }
                }

                const startIndex = currentFullText.length;
                currentFullText += element.text;
                currentPageText += element.text;
                const endIndex = currentFullText.length;

                // Push the final processed item with all necessary metadata
                allProcessedItems.push({
                    text: element.text,
                    boxCoordinates: element.coordinates,
                    img_key: img_key,
                    fileIndex: fileIndex,
                    startIndex: startIndex,
                    endIndex: endIndex,
                });
            });

            // After processing all elements on the page, store the page's text
            this.pagesText.push(currentPageText);
        });

        this.fullText = currentFullText;
        this.processedData = allProcessedItems;
    }

    /**
     * @returns {string} The full, concatenated text from all pages and tables.
     */
    getFullText() {
        return this.fullText;
    }

    /**
     * @returns {Array<string>} An array of strings, where each string is the full text of a single page.
     */
    getPagesText() {
        return this.pagesText;
    }

    /**
     * @private
     * Finds all occurrences of a substring and returns the original processed items that constitute each match.
     * This method does not need changes as it operates on the final `fullText` and `processedData`.
     * @param {string} substring - The text to search for.
     * @returns {Array<Array<Object>>} An array of matches, where each match is an array of processed items.
     */
    _findOriginalBoxes(substring) {
        if (!substring || typeof substring !== 'string') return [];
        const results = [];
        let searchStartIndex = 0;
        let foundIndex;
        while ((foundIndex = this.fullText.indexOf(substring, searchStartIndex)) !== -1) {
            const foundEndIndex = foundIndex + substring.length;
            const matchingBoxes = this.processedData.filter(item => {
                // Check for any overlap between the item's range and the found substring's range
                return item.startIndex < foundEndIndex && item.endIndex > foundIndex;
            });
            if (matchingBoxes.length > 0) {
                results.push(matchingBoxes);
            }
            // Move to the next character to find overlapping matches
            searchStartIndex = foundIndex + 1;
        }
        return results;
    }

    /**
     * Finds all occurrences of a substring and for each, aggregates the bounding boxes
     * of the corresponding text chunks into a single bounding box per image.
     * This method does not need changes because its logic is independent of the source data structure.
     * @param {string} substring - The text to search for.
     * @returns {Array<Array<Object>>} An array of matches. Each match is an array of aggregated box objects,
     * one for each image the match spans across.
     */
    findAndAggregateBoxes(substring) {
        // 1. Find all original items that make up the substring
        const foundMatches = this._findOriginalBoxes(substring);

        if (!foundMatches || foundMatches.length === 0) {
            return [];
        }

        // 2. Process each match to aggregate boxes
        return foundMatches.map(match => {
            // Group the original items by image key
            const boxesByImage = new Map();
            for (const box of match) {
                if (!boxesByImage.has(box.img_key)) {
                    boxesByImage.set(box.img_key, {
                        fileIndex: box.fileIndex,
                        boxes: []
                    });
                }
                boxesByImage.get(box.img_key).boxes.push(box);
            }

            // For each image in this match, compute the encompassing bounding box
            const aggregatedBoxesForMatch = [];
            for (const [img_key, { fileIndex, boxes: boxesInImage }] of boxesByImage.entries()) {
                let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

                for (const box of boxesInImage) {
                    for (const [x, y] of box.boxCoordinates) {
                        minX = Math.min(minX, x);
                        maxX = Math.max(maxX, x);
                        minY = Math.min(minY, y);
                        maxY = Math.max(maxY, y);
                    }
                }

                aggregatedBoxesForMatch.push({
                    aggregatedCoordinates: [
                        [minX, minY], // Top-left
                        [maxX, minY], // Top-right
                        [maxX, maxY], // Bottom-right
                        [minX, maxY]  // Bottom-left
                    ],
                    img_key: img_key,
                    fileIndex: fileIndex,
                    originalBoxes: boxesInImage // Keep original items for debugging or further processing
                });
            }

            return aggregatedBoxesForMatch;
        });
    }
}


export function showRowBoxImg(row, project_id, pxRatioList) {
    // The `row.boxes` property should be the result from `findAndAggregateBoxes`
    const matchBoxes = row.boxes

    // Check if there is at least one match, and the first match has at least one aggregated box
    if (matchBoxes && matchBoxes.length > 0 && matchBoxes[0] && matchBoxes[0].length > 0) {
        // Use the first aggregated box from the first match
        const firstAggregatedBox = matchBoxes[0][0];
        const boxCoordinates = firstAggregatedBox.aggregatedCoordinates;
        const targetKey = firstAggregatedBox.img_key;
        const fileIndex = firstAggregatedBox.fileIndex;
        if (!targetKey || !boxCoordinates || boxCoordinates.length < 4) {
            console.warn("Target key or box coordinates are invalid", firstAggregatedBox);
            ElMessage.warning("此参数没有可用的定位信息");
            return;
        }

        const targetUrl = getFileUrl(project_id, targetKey);
        // console.log(`表格行: boxCoordinates:`, boxCoordinates, `URL:`, targetUrl);
        let ratio = 1
        if (pxRatioList && pxRatioList.length > fileIndex) {
            ratio = pxRatioList[fileIndex];
        }
        // Convert coordinates from [[x1,y1], [x2,y1], [x2,y2], [x1,y2]] to [x1, y1, x2, y2]
        const targetBox = [boxCoordinates[0][0]*ratio, boxCoordinates[0][1]*ratio, boxCoordinates[2][0]*ratio, boxCoordinates[2][1]*ratio];
        openImageWindowWithBox(targetUrl, targetBox);

    } else {
        ElMessage.warning("此参数没有可用的定位信息");
    }
}